import { useState } from 'react';
import { Calendar, DateInput, DatePicker } from '@zillow/constellation-10';
import { addWeeks, format, isAfter, isBefore, isDate, startOfDay } from 'date-fns';

import {
  AVAILABILITY_LOOKAHEAD_WEEKS,
  CALENDAR_AVAILABILITY_INPUT_FORMAT_STRING,
  CALENDAR_EVENT_DATE_DISPLAY_FORMAT_STRING,
} from '../../../../constants';

export type DatePickerMetaProps = Record<string, unknown>;

export const getDisabledDates = (disabledDates: Array<string>) => {
  return (date: Date) => {
    const isOutOfRange =
      isBefore(date, startOfDay(new Date())) || isAfter(date, addWeeks(new Date(), AVAILABILITY_LOOKAHEAD_WEEKS));
    const isAlreadySelected = disabledDates.some((disabledDate) => {
      const disabledDateObj = new Date(disabledDate);
      return (
        format(date, CALENDAR_AVAILABILITY_INPUT_FORMAT_STRING) ===
        format(disabledDateObj, CALENDAR_AVAILABILITY_INPUT_FORMAT_STRING)
      );
    });
    return isOutOfRange || isAlreadySelected;
  };
};

type FormattedDatePickerInputProps = {
  date: string;
  onChangeDate: (date: string, meta: DatePickerMetaProps) => void;
  dataTestId?: string;
  disabledDates?: Array<string>;
};

const FormattedDatePickerInput = ({
  date,
  onChangeDate,
  dataTestId,
  disabledDates = [],
}: FormattedDatePickerInputProps) => {
  const [isDatePickerCalendarOpened, setIsDatePickerCalendarOpened] = useState(false);

  const handleValueChange = (value: string | Date, meta: DatePickerMetaProps) => {
    const parsedValue = isDate(value) ? format(value, CALENDAR_AVAILABILITY_INPUT_FORMAT_STRING) : value;
    setIsDatePickerCalendarOpened(false);

    onChangeDate(parsedValue, meta);
  };

  return (
    <DatePicker.Root
      closeModalOnDateSelect={true}
      dateFormat={CALENDAR_AVAILABILITY_INPUT_FORMAT_STRING}
      disabledDates={getDisabledDates(disabledDates)}
      maxDate={addWeeks(new Date(), AVAILABILITY_LOOKAHEAD_WEEKS)}
      minDate={new Date()}
      onValueChange={handleValueChange}
      value={format(new Date(date), CALENDAR_EVENT_DATE_DISPLAY_FORMAT_STRING)}
      open={isDatePickerCalendarOpened}
      onOpenChange={(open) => setIsDatePickerCalendarOpened(open)}
    >
      <DatePicker.InputGroup css={{ flexGrow: 1 }}>
        <DateInput
          data-testid={dataTestId ?? 'FormattedDatePickerInput'}
          onFocus={() => setIsDatePickerCalendarOpened(true)}
        />
        <DatePicker.Trigger />
      </DatePicker.InputGroup>
      <DatePicker.Portal>
        <DatePicker.Backdrop>
          <DatePicker.Content data-testid="FormattedDatePickerInput-Calendar">
            <Calendar selected={date} />
          </DatePicker.Content>
        </DatePicker.Backdrop>
      </DatePicker.Portal>
    </DatePicker.Root>
  );
};

export default FormattedDatePickerInput;
